import { ChangeEvent, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { useFieldArray } from 'react-hook-form';
import { t } from '../../../services/translationService';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { ChangedProperty, keyValue } from '../../../redux/types/types';
import {
  getBooleanOptions,
  getChangedInformation,
  getDragClass,
  getDragContainerClass,
  getItemStyle,
  getOptions,
  getFilteredOptions,
  getOptionsLabel,
  hasChanged,
  shouldBeLocked,
} from '../common';
import { InformationIcon } from '../../shared/InformationIcon';


interface Props {
  errors?: any;
  register?: any;
  blockRef?: any;
  setValue?: any;
  getValues?: any;
  pageVariant?: string;
  lockedFields?: keyValue[];
  changedProps?: ChangedProperty[];
  watch?: any;
  control?: any;
  trigger?: any;
  visible?: boolean
}

interface epubTillganlighetsTyp {
  kod: string,
  beskrivning: string
}

export const Epub = ({
  errors,
  register,
  blockRef,
  setValue,
  getValues,
  control,
  trigger,
  visible
}: Props) => {

  // State variables
  const [epubsAttributState, setEpubsAttributState] = useState('');
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(undefined);
  const [showEpubUsageModal, setShowEpubUsageModal] = useState<boolean>(false);
  const [showEpubAccessibilityModal, setShowEpubAccessibilityUsageModal] = useState<boolean>(false);

  // Field arrays
  const {
    fields: epubTillganglighetsTyp,
    append: appendEpubTillganglighetstyp,
    update: updateEpubTillganglighetstyp,
    remove: removeEpubTillganglighetstyp,
  } = useFieldArray({
    control,
    name: 'epubTillganglighetsTyp',
  });

  const {
    fields: epubsAttribut,
    append: appendEpubsAttribut,
    remove: removeEpubsAttribut,
    move: moveEpubsAttribut,
  } = useFieldArray({
    control,
    name: 'epubsAttribut',
  });

  const {
    fields: epubAnvandningsomrade,
    append: appendEpubAnvandningsomrade,
    update: updateEpubAnvandningsomrade,
    remove: removeEpubAnvandningsomrade,
  } = useFieldArray({
    control,
    name: 'epubAnvandningsomrade'
  });

  useEffect(() => {
    if (!visible) {
      // clear values
      setValue("epubversion", "");

      // clear value lists
      removeEpubAnvandningsomrade();
      removeEpubsAttribut();
      removeEpubTillganglighetstyp()
    }

  }, [visible, setValue, removeEpubAnvandningsomrade, removeEpubsAttribut, removeEpubTillganglighetstyp])

  const addEpubsAttribut = () => {
    if (epubsAttributState) {
      appendEpubsAttribut(epubsAttributState);
      setEpubsAttributState('');
    }
  };

  const updateEpubsAttribut = (event: ChangeEvent<HTMLInputElement>): void => {
    let value: string = (event.target as HTMLInputElement).value;
    setEpubsAttributState(value);
  };

  const onEpubsAttributeDragEnd = (result: any) => {
    moveEpubsAttribut(result.source.index, result.destination.index);
  };

  const saveEpubTillganglighetstyp = () =>
    trigger(
      `epubTillganglighetsTyp.${currentIndex !== undefined ? currentIndex : epubTillganglighetsTyp.length - 1
      }`
    ).then((validResult: boolean) => {
      if (validResult) {

        const last = currentIndex === undefined
          ? getValues(`epubTillganglighetsTyp.${epubTillganglighetsTyp.length - 1}`)
          : getValues(`epubTillganglighetsTyp.${currentIndex}`);

        currentIndex === undefined && removeEpubTillganglighetstyp(epubTillganglighetsTyp.length - 1);
        currentIndex !== undefined
          ? updateEpubTillganglighetstyp(
            currentIndex,
            {
              ...last
            }
          )
          : appendEpubTillganglighetstyp({
            ...last
          });

        setShowEpubAccessibilityUsageModal(false);
      }
    });

  const saveEpubAnvandningsomrade = () =>
    trigger(
      `epubAnvandningsomrade.${currentIndex !== undefined ? currentIndex : epubAnvandningsomrade.length - 1
      }`
    ).then((validResult: boolean) => {
      if (validResult) {

        const last = currentIndex === undefined
          ? getValues(`epubAnvandningsomrade.${epubAnvandningsomrade.length - 1}`)
          : getValues(`epubAnvandningsomrade.${currentIndex}`);

        currentIndex === undefined && removeEpubAnvandningsomrade(epubAnvandningsomrade.length - 1);
        currentIndex !== undefined
          ? updateEpubAnvandningsomrade(
            currentIndex,
            {
              ...last
            }
          )
          : appendEpubAnvandningsomrade({
            ...last
          });

        setShowEpubUsageModal(false);
      }
    });

  const displayEpubUsageModal = (index?: number) => {
    index === undefined ? appendEpubAnvandningsomrade({}) : setCurrentIndex(index);
    setShowEpubUsageModal(true);
  };

  const closeEpubUsageModal = () => {
    setShowEpubUsageModal(false);
    currentIndex === undefined && removeEpubAnvandningsomrade(epubAnvandningsomrade.length - 1);
  };

  const displayEpubAccessibilityModal = (index?: number) => {
    index === undefined ? appendEpubTillganglighetstyp({}) : setCurrentIndex(index);
    setShowEpubAccessibilityUsageModal(true);
  };

  const closeEpubAccessibilityModal = () => {
    setShowEpubAccessibilityUsageModal(false);
    currentIndex === undefined && removeEpubTillganglighetstyp(epubTillganglighetsTyp.length - 1);
  };

  return visible ? (
    <>
      <h3 ref={blockRef}>{t('epub')}</h3>
      <Col xs={12}>
        <hr className="u-text-grey" />
      </Col>
      <Card className="nopadding my-4">
        <Card.Body className="p-3">
          <Row>
            <Col xs={12}>
              {/*Version*/}
              <Form.Group className="mb-3">
                <Form.Label className="mb-1 text-body">
                  {t('epubversion')}
                </Form.Label>
                <Form.Control
                  as={'select'}
                  type="select"
                  className="form-select"
                  placeholder={t('valjEllips')}
                  {...register('epubversion')}
                  isInvalid={!!errors.epubversion}
                >
                  {getOptions('epubVersions')}
                </Form.Control>
              </Form.Group>
              {/*Tillgänglighetsbeskrivning*/}
              <Form.Group className="mb-3">
                <Form.Label className="mb-1 text-body">
                  {t('epubTillganglighetsBeskrivning')}
                </Form.Label>
                <Form.Control
                  placeholder={t('skrivEllips')}
                  {...register('epubTillganglighetsbeskrivning')}
                  isInvalid={!!errors.epubTillganglighetsbeskrivning}
                />
              </Form.Group>
              {/*Tillgänglighetstyp*/}
              <Col xs={12}>
                <Form.Label className="mb-1 text-body">
                  {t('epubTillganglighetsTyp')}
                  <InformationIcon
                    text={t('hjalpEpubTillganglighetsTyp')}
                    inline
                    className="ps-1"
                  />
                </Form.Label>
                <Card className="mb-4 nopadding">
                  <Card.Body>
                    {epubTillganglighetsTyp?.length > 0 && (
                      <div className='mb-3 w-100 table'>
                          <div className="small px-3 py-1 d-flex border-bottom">
                            <div style={{ width: "50%" }}>
                              <span className="d-block">{t("kod")}</span>
                            </div>
                            <div style={{ width: "20%" }}>
                              <span className="d-block">{t("beskrivning")}</span>
                            </div>
                            <div style={{ width: "30%" }}>
                              <span className="d-block"></span>
                            </div>
                          </div>
                        {epubTillganglighetsTyp.map((row: any, index: number) => {
                            return (
                              <div key={row.id} className={`${index % 2 === 1 ? 'u-background-grey' : ''
                                } d-flex gx-0 px-3 u-text-small-medium u-text-grey py-3 align-items-center border-bottom`}>
                                <div style={{ width: "50%" }}>
                                  {`${getOptionsLabel("epubAccessibilityTypes", row.key)}`}
                                </div>
                                <div style={{ width: "20%" }}>
                                  {row.value}
                                </div>
                                <div style={{ width: "30%" }}>
                                  <div className="d-flex gap-3 justify-content-end">
                                    <i
                                      className="bi bi-pencil u-text-primary u-cursor-pointer"
                                      onClick={() => displayEpubAccessibilityModal(index)}
                                    />
                                    <i
                                      className="bi bi-trash3 u-text-error u-cursor-pointer"
                                      onClick={() => removeEpubTillganglighetstyp(index)}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </Card.Body>
                  <Card.Footer>
                    <Button
                      type="button"
                      className="primary"
                      onClick={() => displayEpubAccessibilityModal()}
                    >
                      <i className="bi bi-plus" />
                      {t("laggTill")}
                    </Button>
                  </Card.Footer>
                </Card>
                <Modal
                  show={showEpubAccessibilityModal}
                  centered
                  size="lg"
                  onHide={closeEpubAccessibilityModal}
                  onExited={() => setCurrentIndex(undefined)}
                >
                  <Modal.Header>
                    <p>{t("laggTill")}</p>
                  </Modal.Header>
                  <Modal.Body>
                    {epubTillganglighetsTyp.map((row: any, index: number) => {
                      return (
                        index ===
                        (currentIndex !== undefined
                          ? currentIndex
                          : epubTillganglighetsTyp.length - 1) && (
                          <Row key={row.id}>
                            <Col xs={12}>
                              <Form.Group className="mb-3">
                                <Form.Label className="mb-1 text-body">
                                  {t("kod")}*
                                </Form.Label>
                                <Form.Control
                                  as={'select'}
                                  type="select"
                                  className="form-select"
                                  placeholder={t('valjEllips')}
                                  {...register(`epubTillganglighetsTyp.${index}.key`, {
                                    validate: (value: any) => {
                                      return !!value || !!getValues(`epubTillganglighetsTyp.${index}.key`) ? true : t("valideringEpubTillganglighetstyp");
                                    }
                                  })}
                                  isInvalid={
                                    !!errors.epubTillganglighetsTyp &&
                                    !!errors.epubTillganglighetsTyp[index].key
                                  }
                                >
                                  {getFilteredOptions("epubAccessibilityTypes", getValues("epubTillganglighetsTyp"), false, getValues(`epubTillganglighetsTyp.${index}.key`))}
                                </Form.Control>
                                {!!errors.epubTillganglighetsTyp &&
                                  !!errors.epubTillganglighetsTyp[index].key &&
                                  (
                                    <Form.Control.Feedback type="invalid">
                                    {`${errors.epubTillganglighetsTyp[index].key.message}`}
                                    </Form.Control.Feedback>
                                  )}
                              </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="mb-1 text-body">
                                  {t("beskrivning")}*
                                </Form.Label>
                                <Form.Control
                                  placeholder={t('skrivEllips')}
                                  {...register(`epubTillganglighetsTyp.${index}.value`)}
                                />
                                {!!errors.epubTillganglighetsTyp &&
                                  !!errors.epubTillganglighetsTyp[index].value &&
                                  (
                                    <Form.Control.Feedback type="invalid">
                                    {`${errors.epubTillganglighetsTyp[index].value.message}`}
                                    </Form.Control.Feedback>
                                  )}
                              </Form.Group>
                            </Col>
                          </Row>
                        )
                      );
                    })}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={closeEpubAccessibilityModal}>
                      {t('avbryt')}
                    </Button>
                    <Button variant="primary" onClick={saveEpubTillganglighetstyp}>
                      {t('spara')}
                    </Button>
                  </Modal.Footer>
                </Modal>
              </Col>
              <hr className="u-text-grey" />
              {/*Attribut*/}
              <div className="d-flex align-items-end w-100">
                <Form.Group as={Col} className="mb-3 col me-3">
                  <Form.Label className="mb-1 text-body">
                    {t("epubsAttribut")}
                    <InformationIcon
                      text={t('hjalpEpubsAttribut')}
                      inline
                      className="ps-1"
                    />
                  </Form.Label>
                  <Form.Control
                    as={'select'}
                    type="select"
                    className="form-select"
                    placeholder={t('valjEllips')}
                    value={epubsAttributState}
                    onChange={updateEpubsAttribut}
                  >
                    {getFilteredOptions('epubAttributes', getValues('epubsAttribut'))}
                  </Form.Control>
                </Form.Group>
                <Form.Group as={Col} className="mb-3 col-auto">
                  <Button
                    className="primary"
                    type="button"
                    onClick={addEpubsAttribut}
                  >
                    <i className="bi bi-plus"></i>
                  </Button>
                </Form.Group>
              </div>
              <DragDropContext onDragEnd={onEpubsAttributeDragEnd}>
                <Droppable droppableId="droppableepubsattribut">
                  {(provided: any, snapshot: any) => (
                    <Col
                      xs={12}
                      className={`mb-3 ${getDragContainerClass(
                        snapshot.isDraggingOver
                      )}`}
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {epubsAttribut.map((row: any, index: number) => {
                        return (
                          <Draggable
                            key={row.id}
                            draggableId={row.id}
                            index={index}
                          >
                            {(provided: any, snapshot: any) => (
                              <Row
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style
                                )}
                                key={row.id}
                                className={`${index % 2 === 1 ? 'u-background-grey' : ''
                                  } mt-2 p-1 ${getDragClass(snapshot.isDragging)}`}
                              >
                                <Col
                                  xs={11}
                                  className="d-flex align-items-center"
                                >
                                  {getOptionsLabel("epubAttributes", getValues(`epubsAttribut.${index}`))}
                                </Col>
                                <Col xs={1} className="d-flex align-items-center">
                                  <i
                                    className="bi bi-trash3 u-text-error u-cursor-pointer mx-auto"
                                    onClick={() => removeEpubsAttribut(index)}
                                  />
                                </Col>
                              </Row>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Col>
                  )}
                </Droppable>
              </DragDropContext>
              <hr className="u-text-grey" />
            </Col>
            <Col xs={12}>
              {/*Användningsområde*/}
              <Form.Label className="mb-1 text-body">
                {t('epubsAnvandningsomraden')}
                <InformationIcon
                  text={t('hjalpInnehall')}
                  inline
                  className="ps-1"
                />
              </Form.Label>
              <Card className="mb-4 nopadding">
                <Card.Body>
                  {epubAnvandningsomrade?.length > 0 && (
                    <div className='mb-3 w-100 table'>
                      <div>
                        <div className="small px-3 py-1 d-flex border-bottom">
                          <div style={{ width: "30%" }}>
                            <span className="d-block">{t("kod")}</span>
                          </div>
                          <div style={{ width: "20%" }}>
                            <span className="d-block">{t("beskrivning")}</span>
                          </div>
                          <div style={{ width: "50%" }}>
                            <span className="d-block"></span>
                          </div>
                        </div>
                      </div>
                      <div>
                        {epubAnvandningsomrade.map((row: any, index: number) => {
                          return (
                            <div key={row.id} className={`${index % 2 === 1 ? 'u-background-grey' : ''
                              } d-flex gx-0 px-3 u-text-small-medium u-text-grey py-3 align-items-center border-bottom`}>
                              <div style={{ width: "30%" }}>
                                {getOptionsLabel("epubUses", row.key)}
                              </div>
                              <div style={{ width: "20%" }}>
                                {getOptionsLabel("epubStatuses", row.value)}
                              </div>
                              <div style={{ width: "50%" }}>
                                <div className="d-flex gap-3 justify-content-end">
                                  <i
                                    className="bi bi-pencil u-text-primary u-cursor-pointer"
                                    onClick={() => displayEpubUsageModal(index)}
                                  />
                                  <i
                                    className="bi bi-trash3 u-text-error u-cursor-pointer"
                                    onClick={() => removeEpubAnvandningsomrade(index)}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer>
                  <Button
                    type="button"
                    className="primary"
                    onClick={() => displayEpubUsageModal()}
                  >
                    <i className="bi bi-plus" />
                    {t("laggTill")}
                  </Button>
                </Card.Footer>
              </Card>
              <Modal
                show={showEpubUsageModal}
                centered
                size="lg"
                onHide={closeEpubUsageModal}
                onExited={() => setCurrentIndex(undefined)}
              >
                <Modal.Header>
                  <p>{t("laggTill")}</p>
                </Modal.Header>
                <Modal.Body>
                  {epubAnvandningsomrade.map((row: any, index: number) => {
                    return (
                      index ===
                      (currentIndex !== undefined
                        ? currentIndex
                        : epubAnvandningsomrade.length - 1) && (
                        <Row key={row.id}>
                          <Col xs={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="mb-1 text-body">
                                {t("kod")}*
                              </Form.Label>
                              <Form.Control
                                as={'select'}
                                type="select"
                                className="form-select"
                                placeholder={t('valjEllips')}
                                {...register(`epubAnvandningsomrade.${index}.key`, {
                                  validate: (value: any) => {
                                    return !!value || !!getValues(`epubAnvandningsomrade.${index}.key`) ? true : t("valideringEpubAnvandningsomrade");
                                  }
                                })}
                                isInvalid={
                                  !!errors.epubAnvandningsomrade &&
                                  !!errors.epubAnvandningsomrade[index].key
                                }
                              >
                                {getFilteredOptions("epubUses", getValues("epubAnvandningsomrade"), false, getValues(`epubAnvandningsomrade.${index}.key`))}
                              </Form.Control>
                              {!!errors.epubAnvandningsomrade &&
                                !!errors.epubAnvandningsomrade[index].key &&
                                (
                                  <Form.Control.Feedback type="invalid">
                                    {`${errors.epubAnvandningsomrade[index].key.message}`}
                                  </Form.Control.Feedback>
                                )}
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="mb-1 text-body">
                                {t("beskrivning")}*
                              </Form.Label>
                              <Form.Control
                                as={'select'}
                                type="select"
                                className="form-select"
                                placeholder={t('valjEllips')}
                                {...register(`epubAnvandningsomrade.${index}.value`, {
                                  validate: (value: any) => {
                                    return !!value || !!getValues(`epubAnvandningsomrade.${index}.value`) ? true : t("valideringEpubAnvandningsomradeStatus");
                                  }
                                })}
                                isInvalid={
                                  !!errors.epubAnvandningsomrade &&
                                  !!errors.epubAnvandningsomrade[index].value
                                }
                              >
                                {getOptions("epubStatuses")}
                              </Form.Control>
                              {!!errors.epubAnvandningsomrade &&
                                !!errors.epubAnvandningsomrade[index].value &&
                                (
                                  <Form.Control.Feedback type="invalid">
                                  {`${errors.epubAnvandningsomrade[index].value.message}`}
                                  </Form.Control.Feedback>
                                )}
                            </Form.Group>
                          </Col>
                        </Row>
                      )
                    );
                  })}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={closeEpubUsageModal}>
                    {t('avbryt')}
                  </Button>
                  <Button variant="primary" onClick={saveEpubAnvandningsomrade}>
                    {t('spara')}
                  </Button>
                </Modal.Footer>
              </Modal>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  ) : null
};

